import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Mic, Square, X } from 'lucide-react'
import type { ShortVideo } from '@services/firebase/short-videos'
import { InlineAudioPlayer } from '@shared/media/audio/InlineAudioPlayer'
import { useAudioRecorder } from '@shared/media/recording/useAudioRecorder'
import { useSpeechTranscriber } from '@shared/media/speech/useSpeechTranscriber'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerTitle,
  Field,
  FieldLabel,
  FieldMessage,
  LmsButton,
  LmsTextarea,
} from '@shared/ui'
import {
  LearningTemplateExerciseResultBar,
  LearningTemplateIncorrectNote,
} from '@features/learning/shared/templates'
import {
  getShortVideoTranslationText,
  isShortVideoPracticeMatch,
  normalizeShortVideoLocale,
} from '@features/short-videos/shared/short-video-helpers'

type MicrophoneStatus = 'idle' | 'requesting' | 'ready' | 'blocked'

function getPracticeBlockedMessage(error?: unknown) {
  if (error instanceof Error) {
    if (
      error.name === 'NotAllowedError' ||
      error.name === 'NotFoundError' ||
      error.name === 'NotReadableError' ||
      /not-allowed|denied|requested device not found|permission/i.test(error.message)
    ) {
      return 'Your browser is not supported, please try switching to another browser.'
    }

    return error.message
  }

  if (
    typeof error === 'string' &&
    /not-allowed|denied|requested device not found|permission/i.test(error)
  ) {
    return 'Your browser is not supported, please try switching to another browser.'
  }

  return typeof error === 'string'
    ? error
    : 'Your browser is not supported, please try switching to another browser.'
}

function getPracticeResultClass({
  expectedWords,
  expectedWordIndex,
  transcript,
}: {
  readonly expectedWords: string[]
  readonly expectedWordIndex: number
  readonly transcript: string
}) {
  const normalizeWord = (value: string) => value.toLowerCase().replace(/[$@%!?,.]/g, '').trim()
  const expectedWord = normalizeWord(expectedWords[expectedWordIndex] ?? '')
  const transcriptWords = transcript
    .split(/\s+/)
    .map(normalizeWord)
    .filter(Boolean)
  const duplicateIndexes = expectedWords.reduce<number[]>((indexes, word, index) => {
    if (normalizeWord(word) === expectedWord) {
      indexes.push(index)
    }

    return indexes
  }, [])
  const currentMatchIndex = duplicateIndexes.indexOf(expectedWordIndex)

  return transcriptWords.filter((word) => word === expectedWord)[currentMatchIndex]
    ? 'correct'
    : 'line-through incorrect'
}

export function ShortVideoPracticeDialog({
  locale,
  onClose,
  open,
  video,
}: {
  readonly locale: string
  readonly onClose: () => void
  readonly open: boolean
  readonly video: ShortVideo
}) {
  const [manualAnswer, setManualAnswer] = useState('')
  const [hasFinished, setHasFinished] = useState(false)
  const [microphoneStatus, setMicrophoneStatus] = useState<MicrophoneStatus>('idle')
  const [microphoneMessage, setMicrophoneMessage] = useState<string | null>(null)
  const [practiceErrorMessage, setPracticeErrorMessage] = useState<string | null>(null)
  const previousRecordingStateRef = useRef(false)
  const recorder = useAudioRecorder({ maxDurationMs: 15000 })
  const transcriber = useSpeechTranscriber({
    lang: `${normalizeShortVideoLocale(locale)}-${normalizeShortVideoLocale(locale).toUpperCase()}`,
  })
  const recorderRef = useRef(recorder)
  const transcriberRef = useRef(transcriber)

  const answer = manualAnswer.trim() || transcriber.transcript
  const isCorrect = useMemo(
    () =>
      isShortVideoPracticeMatch({
        actual: answer,
        expected: video.title ?? '',
      }),
    [answer, video.title],
  )
  const translation = getShortVideoTranslationText(video.translates, locale)
  const titleWords = (video.title ?? '').split(/\s+/).filter(Boolean)
  const isMobileOrTablet = useMemo(
    () =>
      typeof navigator !== 'undefined' &&
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      ),
    [],
  )
  const practiceSupported = recorder.isSupported && transcriber.isSupported
  const practiceMessage =
    practiceErrorMessage ??
    microphoneMessage ??
    (recorder.errorMessage ? getPracticeBlockedMessage(recorder.errorMessage) : null) ??
    (transcriber.errorMessage ? getPracticeBlockedMessage(transcriber.errorMessage) : null) ??
    (practiceSupported ? null : 'Your browser is not supported, please try switching to another browser.')
  const shouldShowPracticeRetryGate = !practiceSupported || microphoneStatus === 'blocked'
  const isPracticing = transcriber.isListening || recorder.isRecording

  useEffect(() => {
    recorderRef.current = recorder
  }, [recorder])

  useEffect(() => {
    transcriberRef.current = transcriber
  }, [transcriber])

  useEffect(() => {
    if (previousRecordingStateRef.current && !recorder.isRecording) {
      transcriberRef.current.stopListening()
      setHasFinished(true)
    }

    previousRecordingStateRef.current = recorder.isRecording
  }, [recorder.isRecording])

  const stopPracticeMedia = useCallback(() => {
    recorderRef.current.stopRecording()
    transcriberRef.current.stopListening()
  }, [])

  const resetPracticeState = useCallback(() => {
    stopPracticeMedia()
    recorderRef.current.clearAudioUrl()
    transcriberRef.current.resetTranscript()
    previousRecordingStateRef.current = false
    setManualAnswer('')
    setHasFinished(false)
    setMicrophoneStatus('idle')
    setMicrophoneMessage(null)
    setPracticeErrorMessage(null)
  }, [stopPracticeMedia])

  useEffect(
    () => () => {
      stopPracticeMedia()
      recorderRef.current.clearAudioUrl()
      transcriberRef.current.resetTranscript()
    },
    [stopPracticeMedia],
  )

  const handleStartPractice = async () => {
    if (!practiceSupported) {
      setPracticeErrorMessage(getPracticeBlockedMessage())
      return
    }

    setMicrophoneStatus('requesting')
    setMicrophoneMessage(null)
    setHasFinished(false)
    setPracticeErrorMessage(null)
    setManualAnswer('')
    transcriber.resetTranscript()
    recorder.clearAudioUrl()

    const recordingStarted = await recorder.startRecording()

    if (!recordingStarted) {
      setMicrophoneStatus('blocked')
      return
    }

    setMicrophoneStatus('ready')

    const transcriptionStarted = transcriber.startListening()

    if (!transcriptionStarted) {
      recorder.stopRecording()
      setPracticeErrorMessage(transcriber.errorMessage ?? getPracticeBlockedMessage())
    }
  }

  const handleFinishPractice = () => {
    transcriber.stopListening()
    recorder.stopRecording()
    setHasFinished(true)
  }

  const handleCheckManualAnswer = () => {
    if (!manualAnswer.trim()) {
      return
    }

    setHasFinished(true)
  }

  const handleClose = () => {
    resetPracticeState()
    onClose()
  }

  if (!open) {
    return null
  }

  return (
    <Drawer
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose()
        }
      }}
      open={open}
    >
      <DrawerContent
        className="shortVideoPracticeDialog"
        hideClose
        side="bottom"
      >
        <DrawerTitle className="shortVideoPracticeDialogTitle">Practice speaking</DrawerTitle>
        <DrawerDescription className="sr-only">
          Practice speaking with the short video sentence.
        </DrawerDescription>
        <div className="shortVideoPracticeDialogContent">
          <div className="shortVideoPracticeSurface">
            {practiceMessage ? (
              <div className="shortVideoPracticeAlert" role="alert">
                {practiceMessage}
              </div>
            ) : null}

            {shouldShowPracticeRetryGate ? (
              <div className="shortVideoPracticeRetry">
                <LmsButton
                  isLoading={microphoneStatus === 'requesting'}
                  onClick={() => void handleStartPractice()}
                  variant="primary"
                >
                  Try again
                </LmsButton>
              </div>
            ) : null}

            <div className="shortVideoPracticeCard">
              <div className="shortVideoPracticePrompt">
                <div className="shortVideoPracticePromptHeading">
                  <h3 className="shortVideoPracticeTitle">
                    {answer
                      ? titleWords.map((word, index) => (
                          <span
                            className={getPracticeResultClass({
                              expectedWordIndex: index,
                              expectedWords: titleWords,
                              transcript: answer,
                            })}
                            key={`${word}-${index}`}
                          >
                            {word}
                            {index !== titleWords.length - 1 ? ' ' : ''}
                          </span>
                        ))
                      : video.title ?? 'Short video sentence'}
                  </h3>
                  {video.audio ? <InlineAudioPlayer audio={video.audio} size="small" /> : null}
                </div>
                {translation ? (
                  <p className="shortVideoPracticeTranslation">
                    {translation}
                  </p>
                ) : null}
              </div>

              {recorder.audioUrl ? (
                <div className="shortVideoPracticeAction">
                  <span>Listen to my pronunciation again</span>
                  <InlineAudioPlayer
                    audio={recorder.audioUrl}
                    key={recorder.audioUrl}
                    size="small"
                  />
                </div>
              ) : (
                <div className="shortVideoPracticeAction shortVideoPracticeActiveFeedback">
                  {isPracticing ? (
                    <span>
                      Press Finish when you are done speaking.
                      <span aria-hidden="true" className="shortVideoPracticeLineAudio">
                        <i />
                        <i />
                        <i />
                      </span>
                    </span>
                  ) : (
                    'Let us practice speaking.'
                  )}
                </div>
              )}
            </div>

            <Field>
              <FieldLabel htmlFor="short-video-practice-answer">Captured or typed answer</FieldLabel>
              <LmsTextarea
                id="short-video-practice-answer"
                onChange={(event) => {
                  setManualAnswer(event.target.value)
                }}
                placeholder="Say the sentence aloud, or type it here when speech recognition is unavailable."
                rows={3}
                value={manualAnswer || transcriber.transcript}
              />
              <FieldMessage>
                Speech recognition fills this field automatically when the browser supports it.
              </FieldMessage>
            </Field>

            {hasFinished && answer ? (
              <>
                {!isCorrect ? (
                  <LearningTemplateIncorrectNote
                    embedded
                    isMobileOrTablet={isMobileOrTablet}
                    text={video.title}
                    translationText={translation}
                  />
                ) : null}
                <LearningTemplateExerciseResultBar
                  embedded
                  onNext={handleClose}
                  result={isCorrect ? 'correct' : 'incorrect'}
                />
              </>
            ) : null}
          </div>
        </div>
        <DrawerFooter className="shortVideoPracticeDialogFooter">
          <div className="shortVideoPracticeDialogActions">
            <LmsButton leadingIcon={<X aria-hidden="true" />} onClick={handleClose} variant="outline">
              Close
            </LmsButton>
            {isPracticing ? (
              <LmsButton
                leadingIcon={<Square aria-hidden="true" />}
                onClick={handleFinishPractice}
                variant="secondary"
              >
                Finish
              </LmsButton>
            ) : hasFinished && answer ? null : manualAnswer.trim() ? (
              <LmsButton
                onClick={handleCheckManualAnswer}
                variant="primary"
              >
                Check answer
              </LmsButton>
            ) : (
              <LmsButton
                isLoading={microphoneStatus === 'requesting'}
                leadingIcon={<Mic aria-hidden="true" />}
                onClick={() => void handleStartPractice()}
                variant="primary"
              >
                Read along
              </LmsButton>
            )}
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
